import { AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { QuestionTypes } from '@auto-editor/shared/enums';
import { NodeModel } from '@auto-editor/shared/models';
import { UtilsService } from '@auto-editor/shared/services';
import { Subscription } from 'rxjs';

@Component({
  selector: 'auto-editor-flow-questionnaire',
  standalone: false,
  templateUrl: './flow-questionnaire.component.html',
  styleUrl: './flow-questionnaire.component.scss'
})
export class FlowQuestionnaireComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() dataQuestionnaire;
  @ViewChild('scrollableBox') scrollableBox: ElementRef<HTMLElement>;
  jumps: { start: string; end: string; label: string; options?: any }[] = [];
  lines: any[] = [];
  private scrollParent: HTMLElement | Document;
  private scrollListener: () => void;
  private logicChangedSubscription: Subscription;

  constructor(public utilsService: UtilsService) {}

  ngOnInit(): void {
    this.createView();
    this.logicChangedSubscription = this.utilsService.logicChanged.subscribe(() => this.createView());
  }

  createView() {
    setTimeout(() => {
      this.lines.forEach((line) => line.remove());
      this.jumps = [];
      this.lines = [];
      this.dataQuestionnaire.nodes.forEach((node, index) => {
        if (!this.isNullOrUndefined(node.defaultJumpTo)) {
          this.createJump(node.id, node.defaultJumpTo, undefined,  {
            path: 'arc',
            startSocket: 'left',
            endSocket: 'left',
            startSocketGravity: 120,
            endSocketGravity: 120
          });
        } else {
          const next = this.dataQuestionnaire.nodes[index + 1] ? this.dataQuestionnaire.nodes[index + 1].id : undefined;
          if (next) this.createJump(node.id, next);
        }
        if (node.input?.options) {
          node.input.options.forEach((option) => {
            if (!this.isNullOrUndefined(option.jumpTo))
              this.createJump(node.id, option.jumpTo, option.title[this.utilsService.languageDefault] || option.responseValue, {
                path: 'arc',
                startSocket: 'right',
                endSocket: 'right'
              });
          });
        }
      });
      this.buildLines();
    });
  }

  isNullOrUndefined(o): boolean {
    return o === null || o === undefined;
  }

  private createLabel(text: string) {
    //@ts-ignore
    return LeaderLine.captionLabel(text, {
      color: '#5c77ff',
      outlineColor: '#ffffff',
      fontFamily: "'Exo 2', sans-serif",
      fontSize: '16px',
      fontWeight: '800',
      lineOffset: -15
    });
  }

  createJump(start, end, label?, extraAttributes = {}) {
    extraAttributes = { ...extraAttributes, color: '#5C77FF', size: 2 };
    const found = this.jumps.findIndex((jump) => jump.start === start && jump.end === end);
    if (found !== -1) {
      if (label) {
        if (!this.jumps[found].label) this.jumps[found].label = '[DEFAULT]';
        this.jumps[found].label = this.jumps[found].label + ' | ' + label;
      }
    } else {
      this.jumps.push({ start, end, label, options: extraAttributes });
    }
  }

  buildLines() {
    this.jumps.forEach((jump) => {
      if (jump.label) jump.options.middleLabel = this.createLabel(jump.label);
      this.lines.push(
        //@ts-ignore
        new LeaderLine(document.getElementById(jump.start), document.getElementById(jump.end), jump.options)
      );
    });
  }

  ngAfterViewInit(): void {
    this.scrollParent = this.getScrollParent(this.scrollableBox.nativeElement);
    //@ts-ignore
    this.scrollListener = AnimEvent.add(() => {
      this.lines.forEach((line) => line.position());
    });
    this.scrollParent.addEventListener('scroll', this.scrollListener, false);
  }

  ngOnDestroy(): void {
    this.logicChangedSubscription?.unsubscribe();
    if (this.scrollParent) this.scrollParent.removeEventListener('scroll', this.scrollListener, false);
    this.lines.forEach((line) => line.remove());
  }

  private getScrollParent(node: HTMLElement): HTMLElement | Document {
    let el = node.parentElement;
    while (el) {
      const { overflowY } = getComputedStyle(el);
      if ((overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight) return el;
      el = el.parentElement;
    }
    return document;
  }

  getIcon(node: NodeModel) {
    if (node && node._metadata?.alias) {
      switch (node._metadata.alias) {
        case 'Group':
        case QuestionTypes.QMatrix:
        case QuestionTypes.QMatrixNumeric:
          if (node.nodes.length === 0) return 'filter_none';
          else if (node.nodes.length > 9) return 'filter_9_plus';
          else return `filter_${node.nodes.length}`;
        case QuestionTypes.QSectionBreak:
          return 'keyboard_tab';
        case QuestionTypes.QContentSection:
          return 'chrome_reader_mode';
        case QuestionTypes.QComment:
          return 'format_align_justify';
        case QuestionTypes.QCover:
          return 'start';
        case QuestionTypes.QLegalAgreement:
          return 'checklist';
        case 'QOptionsImage':
          return 'image';
        case QuestionTypes.QButtons:
        case QuestionTypes.QSmileys:
          return 'sentiment_very_satisfied';
        case QuestionTypes.QNPS:
          return 'speed';
        case 'QOptionsTable':
          return 'format_list_bulleted';
        case QuestionTypes.QOptions:
          return 'radio_button_checked';
        case QuestionTypes.QOptionsMultiple:
          return 'check_box';
        case QuestionTypes.QNumeric:
          return 'star_half';
        case QuestionTypes.QCSAT:
          return 'exposure';
        case QuestionTypes.QFacesOld:
          return 'sentiment_satisfied';
        case QuestionTypes.QSlider:
          return 'commit';
        case QuestionTypes.QTAQTSatisfaction:
          return 'mood';
        case QuestionTypes.QNFC:
          return 'nfc';
        case QuestionTypes.QActions:
          return 'ads_click';
        default:
          return 'settings';
      }
    }
  }
}
